import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  IconButton,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

export interface PatientTagRow {
  id: string;
  name: string;
  color: string | null;
  unit_id?: string;
  created_at?: string;
}

interface ClinicPatientTagsProps {
  patientId?: string | null;
  compact?: boolean;
  /** Ocupa só a largura necessária, para ficar na mesma linha de outras informações. */
  inline?: boolean;
  /** Ao lado do nome no mobile: campo estreito, chips resumidos. */
  headerInline?: boolean;
}

function tagsBase(): string {
  return `${getApiUrl()}/clinic/patient-tags`;
}

export default function ClinicPatientTags({ patientId, compact, inline, headerInline }: ClinicPatientTagsProps) {
  const [allTags, setAllTags] = useState<PatientTagRow[]>([]);
  const [assigned, setAssigned] = useState<PatientTagRow[]>([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      const res = await axios.get(tagsBase());
      if (res.data?.success) setAllTags(res.data.data || []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Erro ao carregar tags');
    } finally {
      setLoadingAll(false);
    }
  }, []);

  const loadPatient = useCallback(async () => {
    if (!patientId) {
      setAssigned([]);
      return;
    }
    setLoadingPatient(true);
    try {
      const res = await axios.get(`${tagsBase()}/patients/${patientId}`);
      if (res.data?.success) setAssigned(res.data.data || []);
    } catch {
      setAssigned([]);
    } finally {
      setLoadingPatient(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    loadPatient();
  }, [loadPatient]);

  const assignedIds = useMemo(() => new Set(assigned.map((t) => t.id)), [assigned]);
  const pickOptions = useMemo(
    () => allTags.filter((t) => !assignedIds.has(t.id)),
    [allTags, assignedIds],
  );

  const assignTag = async (tagId: string) => {
    if (!patientId) return;
    setError('');
    try {
      await axios.post(`${tagsBase()}/patients/${patientId}/assign`, { tag_id: tagId });
      await Promise.all([loadPatient(), loadAll()]);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Não foi possível aplicar a tag');
    }
  };

  const removeTag = async (tagId: string) => {
    if (!patientId) return;
    setError('');
    try {
      await axios.delete(`${tagsBase()}/patients/${patientId}/tags/${tagId}`);
      await loadPatient();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Não foi possível remover a tag');
    }
  };

  const handleCreateTag = async () => {
    const name = newName.trim().replace(/\s+/g, ' ');
    if (!name || !patientId) return;
    setSaving(true);
    setError('');
    try {
      await axios.post(`${tagsBase()}/patients/${patientId}/assign`, { name });
      setCreateOpen(false);
      setNewName('');
      await Promise.all([loadAll(), loadPatient()]);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Não foi possível criar a tag');
    } finally {
      setSaving(false);
    }
  };

  if (!patientId) return null;

  const busy = loadingAll || loadingPatient;
  const chipHeight = headerInline ? 20 : compact ? 22 : 26;
  const chipFont = headerInline ? '0.58rem' : compact ? '0.65rem' : '0.72rem';
  const visibleAssigned = headerInline ? assigned.slice(0, 1) : assigned;
  const extraAssignedCount = headerInline ? Math.max(0, assigned.length - visibleAssigned.length) : 0;

  return (
    <Box
      sx={
        headerInline
          ? { display: 'flex', alignItems: 'center', minWidth: 0, flex: '1 1 auto', maxWidth: '100%', overflow: 'hidden' }
          : inline
            ? { display: 'inline-flex', maxWidth: '100%', minWidth: 0 }
            : { width: '100%', maxWidth: '100%' }
      }
    >
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ py: 0, mb: 0.5, fontSize: '0.75rem' }}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: headerInline ? 'nowrap' : 'wrap',
          gap: headerInline ? 0.35 : 0.5,
          alignItems: 'center',
          minWidth: 0,
          width: headerInline ? '100%' : undefined,
        }}
      >
        {busy && assigned.length === 0 ? (
          <CircularProgress size={headerInline ? 14 : 16} />
        ) : (
          <>
            {visibleAssigned.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                variant="outlined"
                onDelete={headerInline ? undefined : () => removeTag(tag.id)}
                sx={{
                  height: chipHeight,
                  fontSize: chipFont,
                  fontWeight: 500,
                  color: '#475569',
                  bgcolor: '#f8fafc',
                  borderColor: '#e2e8f0',
                  borderRadius: '6px',
                  maxWidth: headerInline ? 72 : undefined,
                  flexShrink: headerInline ? 1 : undefined,
                  '& .MuiChip-label': { px: headerInline ? 0.5 : 1, overflow: 'hidden', textOverflow: 'ellipsis' },
                  '& .MuiChip-deleteIcon': {
                    color: '#94a3b8',
                    fontSize: compact ? 14 : 16,
                    '&:hover': { color: '#64748b' },
                  },
                }}
              />
            ))}
            {extraAssignedCount > 0 && (
              <Chip
                label={`+${extraAssignedCount}`}
                size="small"
                sx={{
                  height: chipHeight,
                  fontSize: chipFont,
                  flexShrink: 0,
                  bgcolor: '#f1f5f9',
                  color: '#64748b',
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}
          </>
        )}
        <Autocomplete
          size="small"
          sx={{
            minWidth: headerInline ? 56 : compact ? 100 : 140,
            maxWidth: headerInline ? 100 : 220,
            flex: headerInline ? '1 1 56px' : undefined,
            flexShrink: headerInline ? 1 : 0,
          }}
          options={pickOptions}
          getOptionLabel={(o) => o.name}
          loading={loadingAll}
          onChange={(_, v) => {
            if (v) assignTag(v.id);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Tag…"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingAll ? <CircularProgress color="inherit" size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: headerInline ? 26 : compact ? 28 : 32,
                  fontSize: chipFont,
                  py: 0,
                },
                '& .MuiOutlinedInput-input': {
                  py: headerInline ? '2px !important' : undefined,
                  px: headerInline ? '6px !important' : undefined,
                },
              }}
            />
          )}
          clearOnBlur
          selectOnFocus
          handleHomeEndKeys
        />
        {!headerInline && (
          <Tooltip title="Adicionar tag">
            <IconButton size="small" onClick={() => setCreateOpen(true)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nova tag do paciente</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Texto (ex.: Maria - mãe)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateTag} disabled={saving || !newName.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Criar e aplicar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
